import React, { useState, useEffect, useMemo } from 'react';
import { MdExpandMore, MdExpandLess, MdRefresh } from 'react-icons/md';
import PlotlyChart from '../../components/charts/PlotlyChart';
import MetricCard from '../../components/shared/MetricCard';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import { hfService } from '../../services/hfService';
import { formatPct, formatNum, formatDate, parseDfJson } from '../../utils/formatters';
import { getFundColor } from '../../utils/colors';

const CollapsibleSection = ({ title, children, defaultOpen = true, onRerun, rerunning }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl mb-4">
      <div className="flex items-center justify-between px-4 md:px-5 py-3">
        <button onClick={() => setOpen(!open)} className="flex items-center gap-2 text-left flex-1">
          <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200">{title}</h3>
          {open ? <MdExpandLess className="text-gray-400 text-xl" /> : <MdExpandMore className="text-gray-400 text-xl" />}
        </button>
        {onRerun && open && (
          <button
            onClick={onRerun}
            disabled={rerunning}
            className="p-1.5 rounded-md text-gray-400 hover:text-brand-gold hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            title="Rerun (bypass cache)"
          >
            <MdRefresh className={`text-lg ${rerunning ? 'animate-spin' : ''}`} />
          </button>
        )}
      </div>
      {open && <div className="px-4 md:px-5 pb-4 md:pb-5">{children}</div>}
    </div>
  );
};

const CUM_PERIODS = [
  { label: '6M', months: 6 },
  { label: '1Y', months: 12 },
  { label: '2Y', months: 24 },
  { label: '3Y', months: 36 },
  { label: '5Y', months: 60 },
  { label: '10Y', months: 120 },
  { label: 'All', months: null },
  { label: 'Custom', months: 'custom' },
];

// Trailing-window tenures the summary response already carries per metric ("<label> | <period>").
// Drives the Overview metric-tile period toggle (SI = focus-fund inception). 'Custom' is appended
// dynamically only when a custom [start,end] window is active (that column is present then).
// 'SI' = focus-fund inception window; 'Full' = each fund's own full-history since-inception.
const TILE_PERIODS = ['SI', 'Full', '1Y', '3Y', '5Y', '10Y'];

const OverviewPage = ({ analyticsParams, colors: propColors, cachedFetch, focusFund }) => {
  const [summaryData, setSummaryData] = useState(null);
  const [cumulativeData, setCumulativeData] = useState(null);
  const [strategySummary, setStrategySummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortCol, setSortCol] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);
  const [stratSortCol, setStratSortCol] = useState(null);
  const [stratSortAsc, setStratSortAsc] = useState(true);
  const [cumPeriod, setCumPeriod] = useState('focus'); // default: focus fund inception
  const [tilePeriod, setTilePeriod] = useState('SI'); // metric-tile tenure (SI/1Y/3Y/5Y/10Y/Custom)
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [rerunning, setRerunning] = useState(false);
  const [expandedStrat, setExpandedStrat] = useState(null);
  const [expandedFund, setExpandedFund] = useState(null);
  const [showBM, setShowBM] = useState(true);
  const [stratTableExpanded, setStratTableExpanded] = useState(false);
  const [fundTableExpanded, setFundTableExpanded] = useState(false);
  // Opt-in custom [start,end] window for the summary table — adds ONE "Custom" column per metric
  // group, only when BOTH months are set (so the default table stays uncluttered). Month inputs
  // (YYYY-MM) since fund returns are monthly.
  const [customRange, setCustomRange] = useState({ start: '', end: '' });

  useEffect(() => {
    if (!analyticsParams?.dataset_id) return;

    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Strategy summary always uses ALL funds (not filtered by selection)
        const allFundsParams = { ...analyticsParams, funds: [] };
        const custom = (customRange.start && customRange.end)
          ? { custom_start: customRange.start, custom_end: customRange.end } : {};
        const summaryParams = { ...analyticsParams, focus_fund: focusFund || undefined, ...custom };
        const [summaryRes, cumulativeRes, stratRes] = await Promise.all([
          hfService.getSummary(summaryParams),
          hfService.getCumulative(analyticsParams),
          hfService.getStrategySummary(allFundsParams).catch(() => null),
        ]);
        if (cancelled) return;
        setSummaryData(summaryRes.data);
        setCumulativeData(cumulativeRes.data);
        setStrategySummary(stratRes?.data || null);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [analyticsParams, focusFund, customRange]);

  // Rerun handler — bypasses backend cache
  const handleRerun = async () => {
    if (!analyticsParams?.dataset_id) return;
    setRerunning(true);
    try {
      const nocacheParams = { ...analyticsParams, nocache: true };
      const custom = (customRange.start && customRange.end)
        ? { custom_start: customRange.start, custom_end: customRange.end } : {};
      const allFundsNocache = { ...analyticsParams, funds: [], nocache: true };
      const [summaryRes, cumulativeRes, stratRes] = await Promise.all([
        hfService.getSummary({ ...nocacheParams, focus_fund: focusFund || undefined, ...custom }),
        hfService.getCumulative(nocacheParams),
        hfService.getStrategySummary(allFundsNocache).catch(() => null),
      ]);
      setSummaryData(summaryRes.data);
      setCumulativeData(cumulativeRes.data);
      setStrategySummary(stratRes?.data || null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setRerunning(false);
    }
  };

  // Derive color map from API or props
  const colorMap = useMemo(() => {
    return cumulativeData?.colors || summaryData?.colors || propColors || {};
  }, [cumulativeData, summaryData, propColors]);

  // Build metric card data from summary table
  const metrics = useMemo(() => {
    if (!summaryData?.table) return null;
    const { index: funds, columns, data } = parseDfJson(summaryData.table);

    const fundCount = funds.filter(f => f !== 'SPX Index').length;

    // Column structure is "<Label> | <Period>" (e.g. "Return | SI"). Tiles reslice by the SELECTED
    // tenure (tilePeriod) — best/worst/medians all read it via this default. SI = focus-fund inception.
    const findCol = (label, period = tilePeriod) => columns.indexOf(`${label} | ${period}`);
    const annRetIdx = findCol('Return');
    const latestRetIdx = -1; // No "Latest Month" column in current table

    let latestMonth = 'N/A';
    let bestPerformer = { name: 'N/A', val: -Infinity };
    let worstPerformer = { name: 'N/A', val: Infinity };

    if (annRetIdx >= 0) {
      data.forEach((row, i) => {
        if (funds[i] === 'SPX Index') return;
        const v = row[annRetIdx];
        if (v != null && v > bestPerformer.val) {
          bestPerformer = { name: funds[i], val: v };
        }
        if (v != null && v < worstPerformer.val) {
          worstPerformer = { name: funds[i], val: v };
        }
      });
    }

    // Try to find latest date from cumulative data
    if (cumulativeData?.cumulative?.index) {
      const dates = cumulativeData.cumulative.index;
      latestMonth = formatDate(dates[dates.length - 1]);
    }

    // Short month label for best/worst (e.g. "Dec 25")
    let shortMonth = '';
    if (cumulativeData?.cumulative?.index) {
      const dates = cumulativeData.cumulative.index;
      const d = new Date(dates[dates.length - 1]);
      shortMonth = d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    }

    // Compute median stats across selected funds (SI period)
    const sharpeIdx = findCol('Sharpe');
    const maxDdIdx = findCol('Max Drawdown');
    const stdIdx = findCol('Std Dev');

    const medianOf = (colIdx) => {
      if (colIdx < 0) return null;
      const vals = data.map((row, i) => funds[i] === 'SPX Index' ? null : row[colIdx]).filter(v => v != null).sort((a, b) => a - b);
      if (vals.length === 0) return null;
      const mid = Math.floor(vals.length / 2);
      return vals.length % 2 ? vals[mid] : (vals[mid - 1] + vals[mid]) / 2;
    };

    return {
      fundCount,
      latestMonth,
      shortMonth,
      bestName: bestPerformer.name,
      bestVal: bestPerformer.val !== -Infinity ? bestPerformer.val : null,
      worstName: worstPerformer.name,
      worstVal: worstPerformer.val !== Infinity ? worstPerformer.val : null,
      medianReturn: medianOf(annRetIdx),
      medianSharpe: medianOf(sharpeIdx),
      medianMaxDd: medianOf(maxDdIdx),
      medianStd: medianOf(stdIdx),
    };
  }, [summaryData, cumulativeData, tilePeriod]);

  // Build cumulative chart traces with period filter
  const cumulativeTraces = useMemo(() => {
    if (!cumulativeData?.cumulative) return [];
    const { index: dates, columns: fundNames, data } = parseDfJson(cumulativeData.cumulative);
    let xDates = dates.map(d => new Date(d));

    // Find focus fund inception date (first non-null data point)
    const focusIdx = focusFund ? fundNames.indexOf(focusFund) : -1;
    let focusInceptionDate = null;
    if (focusIdx >= 0) {
      for (let i = 0; i < data.length; i++) {
        if (data[i][focusIdx] != null) {
          focusInceptionDate = xDates[i];
          break;
        }
      }
    }

    // Period filter helper: slice and rebase
    const sliceAndRebase = (xArr, yArr) => {
      let cutoff = null;

      if (cumPeriod === 'focus') {
        // Default: start from focus fund inception
        cutoff = focusInceptionDate;
      } else if (cumPeriod === 'custom') {
        cutoff = customStart ? new Date(customStart) : null;
      } else if (cumPeriod === null) {
        // "All" — no filter
        return { x: xArr, y: yArr };
      } else {
        // Numeric months
        cutoff = new Date(xArr[xArr.length - 1]);
        cutoff.setMonth(cutoff.getMonth() - cumPeriod);
      }

      if (!cutoff) return { x: xArr, y: yArr };
      const startIdx = xArr.findIndex(d => d >= cutoff);
      if (startIdx <= 0) return { x: xArr, y: yArr };

      let endIdx = xArr.length;
      if (cumPeriod === 'custom' && customEnd) {
        const endDate = new Date(customEnd);
        endIdx = xArr.findIndex(d => d > endDate);
        if (endIdx < 0) endIdx = xArr.length;
      }

      const slicedX = xArr.slice(startIdx, endIdx);
      const baseVal = yArr[startIdx];
      const slicedY = yArr.slice(startIdx, endIdx).map(v =>
        v != null && baseVal != null ? (1 + v) / (1 + baseVal) - 1 : null
      );
      return { x: slicedX, y: slicedY };
    };

    const traces = fundNames.map((name, ci) => {
      const yRaw = data.map(row => row[ci]);
      const { x, y } = sliceAndRebase(xDates, yRaw);
      // Filter out nulls so Plotly doesn't break lines
      const filtered = x.reduce((acc, xi, i) => {
        if (y[i] != null && !isNaN(y[i])) { acc.x.push(xi); acc.y.push(y[i]); }
        return acc;
      }, { x: [], y: [] });
      return {
        x: filtered.x, y: filtered.y,
        type: 'scatter',
        mode: 'lines',
        name,
        line: { color: getFundColor(name, colorMap), width: 2 },
        hovertemplate: `${name}: %{y:.2%}<extra></extra>`,
      };
    });

    // SPX overlay — clipped to earliest fund inception, togglable
    if (cumulativeData.spx && showBM) {
      const spx = parseDfJson(cumulativeData.spx);
      let spxDates = spx.index.map(d => new Date(d));
      let spxVals = spx.data.map(row => (Array.isArray(row) ? row[0] : row));

      // Find earliest fund data point across all funds
      const earliestFund = xDates.find((d, di) =>
        fundNames.some((_, ci) => data[di]?.[ci] != null)
      );
      if (earliestFund) {
        const clipIdx = spxDates.findIndex(d => d >= earliestFund);
        if (clipIdx > 0) {
          spxDates = spxDates.slice(clipIdx);
          const baseVal = spxVals[clipIdx];
          spxVals = spxVals.slice(clipIdx).map(v =>
            v != null && baseVal != null ? (1 + v) / (1 + baseVal) - 1 : null
          );
        }
      }

      const { x, y } = sliceAndRebase(spxDates, spxVals);
      const pairs = x.map((d, i) => [d, y[i]]).filter(([, v]) => v != null);
      if (pairs.length > 0) {
        traces.push({
          x: pairs.map(([d]) => d),
          y: pairs.map(([, v]) => v),
          type: 'scatter',
          mode: 'lines',
          name: 'SPX Index',
          line: { color: '#DC143C', width: 2, dash: 'dash' },
          hovertemplate: 'SPX: %{y:.2%}<extra></extra>',
        });
      }
    }

    return traces;
  }, [cumulativeData, colorMap, cumPeriod, showBM, focusFund, customStart, customEnd]);

  // Sort logic for summary table (Ann. Return now computed server-side from focus fund inception)
  const sortedTable = useMemo(() => {
    if (!summaryData?.table) return null;
    const { index: funds, columns, data } = parseDfJson(summaryData.table);

    if (sortCol === null) return { funds, columns, data };

    const paired = funds.map((f, i) => ({ fund: f, row: data[i] }));
    paired.sort((a, b) => {
      const va = a.row[sortCol];
      const vb = b.row[sortCol];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      return sortAsc ? va - vb : vb - va;
    });

    return {
      funds: paired.map(p => p.fund),
      columns,
      data: paired.map(p => p.row),
    };
  }, [summaryData, sortCol, sortAsc]);

  const handleSort = (colIdx) => {
    if (sortCol === colIdx) {
      setSortAsc(!sortAsc);
    } else {
      setSortCol(colIdx);
      setSortAsc(false);
    }
  };

  // Sort logic for strategy summary table
  // stratSortCol: -1 = Funds column, 0+ = data columns
  const sortedStratTable = useMemo(() => {
    if (!strategySummary?.table) return null;
    const { index: strats, columns, data } = parseDfJson(strategySummary.table);
    const fc = strategySummary?.fund_counts || {};

    if (stratSortCol === null) return { strats, columns, data };

    const paired = strats.map((s, i) => ({ strat: s, row: data[i], fundCount: fc[s] || 0 }));
    paired.sort((a, b) => {
      let va, vb;
      if (stratSortCol === -1) {
        // Sort by fund count
        va = a.fundCount; vb = b.fundCount;
      } else {
        va = a.row[stratSortCol]; vb = b.row[stratSortCol];
      }
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      return stratSortAsc ? va - vb : vb - va;
    });

    return {
      strats: paired.map(p => p.strat),
      columns,
      data: paired.map(p => p.row),
    };
  }, [strategySummary, stratSortCol, stratSortAsc]);

  const handleStratSort = (colIdx) => {
    if (stratSortCol === colIdx) {
      setStratSortAsc(!stratSortAsc);
    } else {
      setStratSortCol(colIdx);
      setStratSortAsc(false);
    }
  };

  // Determine if a column value is a percentage
  const isPctCol = (colName) => {
    const lower = colName.toLowerCase();
    return lower.includes('return') || lower.includes('drawdown') || lower.includes('vol')
      || lower.includes('std') || lower.includes('pct') || lower.includes('%')
      || lower.includes('latest') || lower.includes('best') || lower.includes('worst')
      || lower.includes('avg') || lower.includes('pain') || lower.includes('var');
  };

  if (loading) return <LoadingSpinner message="Loading overview data..." />;

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-2">Failed to load overview data</p>
        <p className="text-sm text-gray-500 dark:text-gray-400">{error}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Metric Cards — 2-col mobile, 6-col desktop */}
      {metrics && (
        <>
        {/* Metric-tile tenure toggle. The period columns (SI/1Y/3Y/5Y/10Y[/Custom]) are already in the
            summary response, so switching reslices the tiles client-side — instant, no refetch. SI =
            focus-fund inception. Only Sharpe/Best/Worst/Return tiles change; Funds + Data Through don't. */}
        <div className="flex items-center gap-1.5 mb-3 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider text-gray-400 mr-1">Period</span>
          {[...TILE_PERIODS, ...((customRange.start && customRange.end) ? ['Custom'] : [])].map(p => (
            <button
              key={p}
              onClick={() => setTilePeriod(p)}
              className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-md transition-colors ${tilePeriod === p ? 'bg-[#c9a96e] text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
            >
              {p}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
          <MetricCard
            label="Funds"
            value={metrics.fundCount.toLocaleString()}
            subtitle={metrics.latestMonth}
          />
          <MetricCard
            label="Median Sharpe"
            value={metrics.medianSharpe != null ? formatNum(metrics.medianSharpe) : 'N/A'}
            valueColor={metrics.medianSharpe > 1 ? 'text-green-600 dark:text-green-400' : metrics.medianSharpe < 0 ? 'text-red-500 dark:text-red-400' : undefined}
          />
          <MetricCard
            label="Best Fund"
            value={metrics.bestName ? metrics.bestName.split(' ').slice(0, 2).join(' ') : 'N/A'}
            subtitle={metrics.bestVal != null ? formatPct(metrics.bestVal) : 'N/A'}
            valueColor="text-green-600 dark:text-green-400"
          />
          <MetricCard
            label="Worst Fund"
            value={metrics.worstName ? metrics.worstName.split(' ').slice(0, 2).join(' ') : 'N/A'}
            subtitle={metrics.worstVal != null ? formatPct(metrics.worstVal) : 'N/A'}
            valueColor="text-red-500 dark:text-red-400"
          />
          {/* Extra tiles — hidden on mobile, visible on desktop */}
          <MetricCard
            label="Data Through"
            value={metrics.latestMonth}
          />
          <MetricCard
            label="Median Return"
            value={metrics.medianReturn != null ? formatPct(metrics.medianReturn) : 'N/A'}
            valueColor={metrics.medianReturn > 0 ? 'text-green-600 dark:text-green-400' : metrics.medianReturn < 0 ? 'text-red-500 dark:text-red-400' : undefined}
          />
        </div>
        {/* closes the React fragment <> opened above the Period toggle (inside `{metrics && (`) */}
        </>
      )}

      {/* Strategy Summary — always shows all strategies */}
      {sortedStratTable && (
        <CollapsibleSection title="Strategy Summary" onRerun={handleRerun} rerunning={rerunning} defaultOpen={false}>
          {/* Mobile: simplified list OR full table (toggle) */}
          <div className="md:hidden">
            <div className="flex justify-end mb-2">
              <button
                onClick={() => setStratTableExpanded(!stratTableExpanded)}
                className="text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                {stratTableExpanded ? 'Simplify' : 'Expand Table'}
              </button>
            </div>
          </div>
          <div className={`md:hidden ${stratTableExpanded ? 'hidden' : ''}`}>
            {/* Column headers */}
            <div className="flex items-center justify-between py-1.5 mb-1 text-[10px] uppercase tracking-wider text-gray-400 font-medium">
              <span>Strategy</span>
              <div className="flex items-center gap-3">
                <span className="w-8 text-right"># Funds</span>
                <span className="w-16 text-right">Ann. Ret</span>
              </div>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {sortedStratTable.strats.map((strat, ri) => {
                const isSpx = strat === 'SPX Index';
                const annRetIdx = sortedStratTable.columns.findIndex(c => c.startsWith('Ann. Return'));
                const annRet = annRetIdx >= 0 ? sortedStratTable.data[ri]?.[annRetIdx] : null;
                const isExpanded = expandedStrat === strat;
                return (
                  <div key={strat}>
                    <div
                      onClick={() => setExpandedStrat(isExpanded ? null : strat)}
                      className={`flex items-center justify-between py-2.5 cursor-pointer active:bg-gray-50 dark:active:bg-gray-800/30 ${isSpx ? 'border-t border-gray-300 dark:border-gray-600' : ''}`}
                    >
                      <span className={`text-sm font-medium ${isSpx ? 'text-red-500 italic' : 'text-gray-800 dark:text-gray-200'}`}>{strat}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-400 tabular-nums w-8 text-right">{isSpx ? '—' : (strategySummary?.fund_counts?.[strat] ?? '-')}</span>
                        <span className={`text-sm font-semibold tabular-nums w-16 text-right ${annRet > 0 ? 'text-green-600 dark:text-green-400' : annRet < 0 ? 'text-red-500 dark:text-red-400' : 'text-gray-600'}`}>
                          {annRet != null ? (annRet > 0 ? '+' : '') + formatPct(annRet) : 'N/A'}
                        </span>
                      </div>
                    </div>
                    {/* Expanded detail */}
                    {isExpanded && (
                      <div className="pb-3 pl-2 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                        {sortedStratTable.columns.map((col, ci) => {
                          if (col.startsWith('Ann. Return') || col === 'Months of Data') return null;
                          const val = sortedStratTable.data[ri]?.[ci];
                          return (
                            <div key={col} className="flex justify-between">
                              <span className="text-gray-400 truncate mr-2">{col}</span>
                              <span className={`tabular-nums font-medium ${
                                val != null && isPctCol(col) ? (val > 0 ? 'text-green-600 dark:text-green-400' : val < 0 ? 'text-red-500 dark:text-red-400' : 'text-gray-600') : 'text-gray-700 dark:text-gray-300'
                              }`}>
                                {val == null ? 'N/A' : isPctCol(col) ? formatPct(val) : formatNum(val)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
              {/* Overall */}
              <div className="flex items-center justify-between py-2.5 border-t-2 border-brand-gold/30">
                <span className="text-sm font-semibold text-brand-gold">Overall</span>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-400 tabular-nums w-8 text-right">{Object.values(strategySummary?.fund_counts || {}).reduce((a, b) => a + b, 0)}</span>
                  {(() => {
                    const idx = sortedStratTable.columns.findIndex(c => c.startsWith('Ann. Return'));
                    const vals = idx >= 0 ? sortedStratTable.data.map((r, i) => r[idx]).filter((v, i) => sortedStratTable.strats[i] !== 'SPX Index' && v != null) : [];
                    const avg = vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
                    return (
                      <span className={`text-sm font-semibold tabular-nums w-16 text-right ${avg > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>
                        {avg != null ? (avg > 0 ? '+' : '') + formatPct(avg) : 'N/A'}
                      </span>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>

          {/* Desktop full table (also shown on mobile when expanded) */}
          <div className={`${stratTableExpanded ? '' : 'hidden'} md:block overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 scrollbar-thin`}>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/50 text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="px-3 py-2.5 text-left">Strategy</th>
                  <th className="px-3 py-2.5 text-right cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-300" onClick={() => handleStratSort(-1)}>
                    Funds{stratSortCol === -1 && <span className="ml-1">{stratSortAsc ? '\u25B2' : '\u25BC'}</span>}
                  </th>
                  {sortedStratTable.columns.map((col, ci) => (
                    <th
                      key={col}
                      onClick={() => handleStratSort(ci)}
                      className="px-3 py-2.5 text-right cursor-pointer whitespace-nowrap select-none hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      {col}
                      {stratSortCol === ci && (
                        <span className="ml-1">{stratSortAsc ? '\u25B2' : '\u25BC'}</span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {sortedStratTable.strats.map((strat, ri) => {
                  const isSpx = strat === 'SPX Index';
                  return (
                  <tr key={strat} className={isSpx
                    ? 'bg-red-50/50 dark:bg-red-900/10 border-t border-gray-300 dark:border-gray-600'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-800/30'}>
                    <td className={`px-3 py-2 whitespace-nowrap font-medium ${isSpx ? 'text-red-600 dark:text-red-400 italic' : 'text-gray-800 dark:text-gray-200'}`}>
                      {strat}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">
                      {isSpx ? '—' : (strategySummary?.fund_counts?.[strat] ?? '-')}
                    </td>
                    {sortedStratTable.data[ri].map((val, ci) => (
                      <td key={ci} className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${
                        val != null && isPctCol(sortedStratTable.columns[ci])
                          ? val > 0 ? 'text-green-600 dark:text-green-400' : val < 0 ? 'text-red-500 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'
                          : 'text-gray-700 dark:text-gray-300'
                      }`}>
                        {val == null
                          ? 'N/A'
                          : isPctCol(sortedStratTable.columns[ci])
                            ? formatPct(val)
                            : formatNum(val)}
                      </td>
                    ))}
                  </tr>
                  );
                })}
                {/* Overall row */}
                {sortedStratTable.strats.length > 1 && (
                  <tr className="bg-brand-gold/5 dark:bg-brand-gold/10 font-semibold border-t-2 border-brand-gold/30">
                    <td className="px-3 py-2 whitespace-nowrap text-brand-gold">Overall</td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-900 dark:text-gray-100">
                      {Object.values(strategySummary?.fund_counts || {}).reduce((a, b) => a + b, 0)}
                    </td>
                    {sortedStratTable.columns.map((col, ci) => {
                      const vals = sortedStratTable.data.map((row, i) => row[ci]).filter((v, i) => sortedStratTable.strats[i] !== 'SPX Index' && v != null);
                      const avg = vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
                      return (
                        <td key={ci} className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${
                          avg != null && isPctCol(col)
                            ? avg > 0 ? 'text-green-600 dark:text-green-400' : avg < 0 ? 'text-red-500 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'
                            : 'text-gray-900 dark:text-gray-100'
                        }`}>
                          {avg == null ? 'N/A' : isPctCol(col) ? formatPct(avg) : formatNum(avg)}
                        </td>
                      );
                    })}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CollapsibleSection>
      )}

      {/* Fund-level Summary Statistics — above chart */}
      {sortedTable && (() => {
        // Parse "Group | Period" columns to build 2-row header
        // Single columns (no " | ") get rowSpan=2 in top row
        const groups = []; // [{label, span, startIdx, isSingle}]
        sortedTable.columns.forEach((col, ci) => {
          const parts = col.split(' | ');
          const isSingle = parts.length === 1;
          const groupName = parts[0];
          const prev = groups[groups.length - 1];
          if (prev && prev.label === groupName && !prev.isSingle && !isSingle) {
            prev.span++;
          } else {
            groups.push({ label: groupName, span: 1, startIdx: ci, isSingle });
          }
        });
        const getSubLabel = (col) => {
          const parts = col.split(' | ');
          return parts.length > 1 ? parts[1] : '';
        };
        const hasSubHeader = sortedTable.columns.some(c => c.includes(' | '));
        // For mobile simplified view, pick the Ann. Return SI column
        const annRetIdx = sortedTable.columns.findIndex(c => c === 'Return | SI' || c.startsWith('Ann. Return'));
        return (
        <CollapsibleSection title="Fund Summary Statistics">
          {/* Custom date-range — opt-in "Custom" column per metric group (hidden until both months set) */}
          <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
            <span className="uppercase tracking-wider text-gray-400">Custom range</span>
            <input
              type="month" value={customRange.start}
              onChange={(e) => setCustomRange((r) => ({ ...r, start: e.target.value }))}
              className="rounded border border-gray-200 bg-transparent px-2 py-1 dark:border-gray-700"
            />
            <span className="text-gray-400">–</span>
            <input
              type="month" value={customRange.end}
              onChange={(e) => setCustomRange((r) => ({ ...r, end: e.target.value }))}
              className="rounded border border-gray-200 bg-transparent px-2 py-1 dark:border-gray-700"
            />
            {(customRange.start || customRange.end) && (
              <button
                onClick={() => setCustomRange({ start: '', end: '' })}
                className="rounded px-2 py-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >Clear</button>
            )}
            <span className="text-[10px] text-gray-400">
              {customRange.start && customRange.end ? 'adds a “Custom” column' : 'set both months to add a “Custom” column'}
            </span>
          </div>
          {/* Mobile: simplified list OR full table (toggle) */}
          <div className="md:hidden">
            <div className="flex justify-end mb-2">
              <button
                onClick={() => setFundTableExpanded(!fundTableExpanded)}
                className="text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                {fundTableExpanded ? 'Simplify' : 'Expand Table'}
              </button>
            </div>
          </div>
          <div className={`md:hidden ${fundTableExpanded ? 'hidden' : ''}`}>
            {/* Column headers */}
            <div className="flex items-center justify-between py-1.5 mb-1 text-[10px] uppercase tracking-wider text-gray-400 font-medium">
              <span>Fund</span>
              <span className="w-20 text-right">Ann. Ret (SI)</span>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {sortedTable.funds.map((fund, ri) => {
                const isSpx = fund === 'SPX Index';
                const annRet = annRetIdx >= 0 ? sortedTable.data[ri]?.[annRetIdx] : null;
                const isExpanded = expandedFund === fund;
                return (
                  <div key={fund}>
                    <div
                      onClick={() => setExpandedFund(isExpanded ? null : fund)}
                      className={`flex items-center justify-between py-2.5 cursor-pointer active:bg-gray-50 dark:active:bg-gray-800/30 ${isSpx ? 'border-t border-gray-300 dark:border-gray-600' : ''}`}
                    >
                      <span className={`text-sm font-medium truncate pr-2 ${isSpx ? 'text-red-500 italic' : 'text-gray-800 dark:text-gray-200'}`}>{fund}</span>
                      <span className={`text-sm font-semibold tabular-nums w-20 text-right shrink-0 ${annRet > 0 ? 'text-green-600 dark:text-green-400' : annRet < 0 ? 'text-red-500 dark:text-red-400' : 'text-gray-600'}`}>
                        {annRet != null ? (annRet > 0 ? '+' : '') + formatPct(annRet) : 'N/A'}
                      </span>
                    </div>
                    {isExpanded && (
                      <div className="pb-3 pl-2 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                        {sortedTable.columns.map((col, ci) => {
                          if (ci === annRetIdx) return null;
                          const val = sortedTable.data[ri]?.[ci];
                          return (
                            <div key={col} className="flex justify-between">
                              <span className="text-gray-400 truncate mr-2">{col}</span>
                              <span className={`tabular-nums font-medium shrink-0 ${
                                val != null && isPctCol(col) ? (val > 0 ? 'text-green-600 dark:text-green-400' : val < 0 ? 'text-red-500 dark:text-red-400' : 'text-gray-600') : 'text-gray-700 dark:text-gray-300'
                              }`}>
                                {val == null ? 'N/A' : isPctCol(col) ? formatPct(val) : formatNum(val)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Desktop full table (also shown on mobile when expanded) */}
          <div className={`${fundTableExpanded ? '' : 'hidden'} md:block overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 scrollbar-thin`}>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/50 text-xs uppercase tracking-wider text-gray-500">
                {/* Top row: grouped headers */}
                {hasSubHeader && (
                  <tr>
                    <th rowSpan={2} className="px-3 py-2 text-left sticky left-0 z-10 bg-gray-50 dark:bg-gray-800/50 border-b border-r border-gray-200 dark:border-gray-700">Fund</th>
                    {groups.map((g, gi) => (
                      <th key={gi}
                        colSpan={g.isSingle ? 1 : g.span}
                        rowSpan={g.isSingle ? 2 : 1}
                        className={`px-3 py-2 text-center border-b border-gray-200 dark:border-gray-700 ${!g.isSingle && g.span > 1 ? 'border-l border-r' : ''}`}
                      >
                        {g.label}
                      </th>
                    ))}
                  </tr>
                )}
                {/* Sub row: only period labels for grouped columns */}
                <tr>
                  {!hasSubHeader && (
                    <th className="px-3 py-2.5 text-left sticky left-0 z-10 bg-gray-50 dark:bg-gray-800/50">Fund</th>
                  )}
                  {sortedTable.columns.map((col, ci) => {
                    const sub = getSubLabel(col);
                    if (!sub) return null;
                    return (
                      <th key={col} onClick={() => handleSort(ci)}
                        className="px-3 py-2 text-right cursor-pointer whitespace-nowrap select-none hover:text-gray-700 dark:hover:text-gray-300">
                        {sub}{sortCol === ci && <span className="ml-1">{sortAsc ? '\u25B2' : '\u25BC'}</span>}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {sortedTable.funds.map((fund, ri) => {
                  const isSpx = fund === 'SPX Index';
                  return (
                    <tr key={fund} className={isSpx ? 'bg-red-50/30 dark:bg-red-900/10' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'}>
                      <td
                        title={fund}
                        className={`px-3 py-2 font-medium sticky left-0 z-[5] max-w-[140px] md:max-w-none truncate md:whitespace-nowrap md:overflow-visible ${isSpx ? 'text-red-600 dark:text-red-400 bg-red-50/30 dark:bg-red-900/10' : 'text-gray-900 dark:text-gray-100 bg-white dark:bg-[#161b22]'}`}
                      >
                        {fund}
                      </td>
                      {sortedTable.data[ri]?.map((val, ci) => {
                        const col = sortedTable.columns[ci];
                        return (
                          <td key={ci} className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${
                            isPctCol(col) ? (val > 0 ? 'text-green-600 dark:text-green-400' : val < 0 ? 'text-red-500 dark:text-red-400' : '') : ''
                          }`}>
                            {val == null ? 'N/A' : isPctCol(col) ? formatPct(val) : formatNum(val)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CollapsibleSection>
        );
      })()}

      {/* Cumulative Returns Chart with period toggles */}
      {cumulativeTraces.length > 0 && (
        <CollapsibleSection title="Cumulative Returns" onRerun={handleRerun} rerunning={rerunning}>
          <div className="flex gap-1 items-center mb-3 flex-wrap">
            <button
              onClick={() => setCumPeriod('focus')}
              className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                cumPeriod === 'focus'
                  ? 'bg-brand-gold text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >Inception</button>
            {CUM_PERIODS.map(p => (
              <button
                key={p.label}
                onClick={() => setCumPeriod(p.months)}
                className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                  cumPeriod === p.months
                    ? 'bg-brand-gold text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {p.label}
              </button>
            ))}
            {cumPeriod === 'custom' && (
              <div className="flex gap-1 items-center ml-1">
                <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
                  className="text-xs px-2 py-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#0d1117] text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-brand-gold/40" />
                <span className="text-xs text-gray-400">to</span>
                <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
                  className="text-xs px-2 py-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#0d1117] text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-brand-gold/40" />
              </div>
            )}
            <span className="w-px h-4 bg-gray-300 dark:bg-gray-700 mx-1" />
            <button
              onClick={() => setShowBM(!showBM)}
              className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                showBM
                  ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {showBM ? 'SPX ✓' : 'SPX'}
            </button>
          </div>
          <PlotlyChart
            data={cumulativeTraces}
            layout={{
              title: '',
              yaxis: { title: 'Cumulative Return (%)', tickformat: '.0%' },
              xaxis: { title: '' },
              height: 380,
            }}
            style={{ minHeight: 380 }}
          />
        </CollapsibleSection>
      )}

      {/* Old Fund Summary removed — now shown above the chart */}
    </div>
  );
};

export default OverviewPage;
